import {
  CREATE_ORDER_FAILURE,
  CREATE_ORDER_REQUEST,
  CREATE_ORDER_SUCCESS,
  RECURRING_SINGLE_PAYMENT_OPTION_FIELD,
} from 'constants/index';
import { schemas } from 'constants/schemas';
import type { Order } from 'models/order';
import { normalize } from 'normalizr';
import type { RootState } from 'reducers/types';
import { getFieldValue, getFieldValues } from 'selectors';
import { getOrderTemplate } from 'selectors/orderTemplate/orderTemplate';
import type { CreatePayload } from 'services/apiV3/orders/orders';
import { logger } from 'services/logger/logger';
import type { PayexThunkAction } from 'store/configureStore';
import { url } from 'utils/url/url';
import { windowUtils } from 'utils/windowUtils/windowUtils';
import { entitiesPayload } from '../entitiesPayload/entitiesPayload';

const trackingUrl = (id, token) => {
  const domain = url.getCurrentUrl();

  return `${domain}/tracking/${id}?token=${token}`;
};

const buildReturnUrl = state => {
  const recipientReturnUrl = encodeURIComponent(
    getFieldValue(state, 'return_url'),
  );

  return recipientReturnUrl
    ? `${windowUtils.getHref()}?redirect_url=${recipientReturnUrl}`
    : null;
};

export const buildPayloadFromTemplate = (
  orderTemplate,
  state: RootState,
): CreatePayload => {
  const {
    id: _id,
    token: _token,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    returnUrl: _originalReturnUrl,
    ...rest
  } = orderTemplate;

  const { callback: _callback, ...entities } = entitiesPayload(
    ['sender', 'offers'],
    state,
  );
  const returnUrl = buildReturnUrl(state);
  const {
    mute_email_notifications: muteEmailNotifications,
    session_id: sessionId,
    payor_id: payorId,
  } = getFieldValues(state);

  return {
    ...rest,
    ...entities,
    ...(returnUrl && { return_url: returnUrl }),
    ...(muteEmailNotifications && { mute_email_notifications: true }),
    ...(sessionId && { session_id: sessionId }),
    ...(payorId && { metadata: { payor_id: payorId } }),
  };
};

export const buildPayload = (state: RootState): CreatePayload => {
  const entities = entitiesPayload(['sender', 'recipient', 'offers'], state);
  if (
    entities.offer?.id &&
    entities.offer.id.endsWith(`#${RECURRING_SINGLE_PAYMENT_OPTION_FIELD}`)
  ) {
    const splittedId = entities.offer.id.split('#');
    entities.offer.id = splittedId[0];
  }
  const returnUrl = buildReturnUrl(state);
  const {
    recipient: recipientId,
    mute_email_notifications: muteEmailNotifications,
    session_id: sessionId,
    payor_id: payorId,
  } = getFieldValues(state);

  return {
    ...entities,
    ...(recipientId && {
      recipient: { ...entities.recipient, id: recipientId },
    }),
    ...(muteEmailNotifications && { mute_email_notifications: true }),
    ...(returnUrl && { return_url: returnUrl }),
    ...(sessionId && { session_id: sessionId }),
    ...(payorId && { metadata: { payor_id: payorId } }),
  };
};

export const createOrder =
  (): PayexThunkAction<Promise<Order>> =>
  async (dispatch, getState, { orders }) => {
    dispatch({ type: CREATE_ORDER_REQUEST });

    const state = getState();

    const orderTemplate = getOrderTemplate(state);
    let payload = {};

    if (orderTemplate.id) {
      payload = buildPayloadFromTemplate(orderTemplate, state);
    } else {
      payload = buildPayload(state);
    }

    try {
      const response = await orders.create(payload);
      const { id, token } = response;
      await orders.update({
        id,
        token,
        metadata: {
          tracking_url: trackingUrl(id, token),
        },
      });

      dispatch({ type: CREATE_ORDER_SUCCESS, payload: response });
      return response;
    } catch (error) {
      const { parsedBody } = error;

      if (error.status === 422) {
        logger.log({
          description: 'EMBEDDED 2.0 INTEGRATION - ORDER CREATION FAILED',
          parameters: {
            recipient: payload.recipient.id,
            erroredBody: parsedBody,
          },
        });
      }

      const { parsedBody: { errors: validationErrors = [] } = {} } = error;

      dispatch({
        type: CREATE_ORDER_FAILURE,
        payload: normalize(validationErrors, schemas.arrayOfErrors),
      });

      throw error;
    }
  };
